import React, { useEffect, useMemo, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  startAt,
  endAt,
  limit,
  getDocs,
  doc,
  startAfter,
  getDoc,
} from "firebase/firestore";
import {} from "firebase/firestore";

import { firebaseApp } from "../../firebase/firebaseApp";
import LoadingBouncyDots from "../../components/ui/LoadingBouncyDots";
import Spacer from "../../components/ui/Spacer";
import ExertionRecoveryActivityDetails from "../../components/result/ExertionRecoveryActivityDetails";
import SpirometryActivityDetails from "../../components/result/SpirometryActivityDetails";

// import ExertionRecoveryActivityDetails from "../../components/activity/ExertionRecoveryActivityDetails";
// import SpirometryActivityDetails from "../../components/activity/SpirometryActivityDetails";

const USER_LIMIT = 15;
const FILE_PAGE_SIZE = 50;

function normalize(s) {
  return String(s || "")
    .trim()
    .toLowerCase();
}

function baseNameFromFile(filename) {
  const n = String(filename || "");
  return n.endsWith(".json") ? n.slice(0, -5) : n;
}

function detectKind({ filename, data }) {
  const n = normalize(filename);

  // 1) explicit hint from data (if you have it)
  const dk = normalize(data?.kind);
  if (dk === "exertion" || dk === "spirometry") return dk;

  // 2) filename heuristics
  if (n.includes("exertion")) return "exertion";
  if (n.includes("spirometry") || n.includes("forced")) return "spirometry";

  // 3) structure heuristics (fallbacks)
  if (Array.isArray(data?.peak_times_s) || Array.isArray(data?.peak_values))
    return "exertion";

  // spirometry often has flow traces; adapt if your schema differs
  if (
    Array.isArray(data?.flow) ||
    Array.isArray(data?.flow_trace) ||
    Array.isArray(data?.flow_times_s)
  )
    return "spirometry";

  return "unknown";
}

function parseDateFromData(data) {
  const t = data?.timestamp || data?.createdAt || data?.date;
  if (!t) return new Date();
  const d = t instanceof Date ? t : new Date(t);
  return Number.isFinite(d.getTime()) ? d : new Date();
}

export default function AdminInspectMeasurementsPage() {
  const db = useMemo(() => getFirestore(firebaseApp), []);
  const [fileCursor, setFileCursor] = useState(null);
  const [hasMoreFiles, setHasMoreFiles] = useState(true);

  // --- user search + selection ---
  const [emailPrefix, setEmailPrefix] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // --- storage file listing ---
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesErr, setFilesErr] = useState("");
  const [files, setFiles] = useState([]);
  const [nextToken, setNextToken] = useState(null);

  // --- selected file + loaded JSON ---
  const [selectedFile, setSelectedFile] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataErr, setDataErr] = useState("");
  const [activityData, setActivityData] = useState(null);

  // Debounced user search
  useEffect(() => {
    let alive = true;
    const handle = setTimeout(async () => {
      try {
        setUsersLoading(true);

        const prefix = normalize(emailPrefix);
        let qRef;

        if (prefix) {
          qRef = query(
            collection(db, "lungform_users"),
            orderBy("email", "asc"),
            startAt(prefix),
            endAt(prefix + "\uf8ff"),
            limit(USER_LIMIT)
          );
        } else {
          qRef = query(
            collection(db, "lungform_users"),
            orderBy("email", "asc"),
            limit(USER_LIMIT)
          );
        }

        const snap = await getDocs(qRef);
        if (!alive) return;

        setUsers(
          snap.docs.map((d) => ({
            uid: d.id,
            ...(d.data() || {}),
          }))
        );
      } catch (e) {
        // keep quiet; admin page should remain usable
        if (!alive) return;
        setUsers([]);
      } finally {
        if (alive) setUsersLoading(false);
      }
    }, 200);

    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [db, emailPrefix]);

  async function loadFiles({ reset = false } = {}) {
    if (!selectedUser?.uid) return;

    setFilesErr("");
    setFilesLoading(true);

    try {
      const colRef = collection(
        db,
        "measurements",
        selectedUser.uid,
        "breatheultra"
      );

      const qRef = reset
        ? query(colRef, limit(FILE_PAGE_SIZE))
        : query(colRef, startAfter(fileCursor), limit(FILE_PAGE_SIZE));

      const snap = await getDocs(qRef);
      const docs = snap.docs;

      const newItems = docs.map((d) => ({
        id: d.id,
        path: d.ref.path,
      }));

      // If you still want “newest first”, sort by doc id locally
      newItems.sort((a, b) => (a.id < b.id ? 1 : -1));

      setFiles((prev) => (reset ? newItems : [...prev, ...newItems]));
      setFileCursor(docs[docs.length - 1] || null);
      setHasMoreFiles(docs.length === FILE_PAGE_SIZE);
    } catch (e) {
      setFilesErr(e?.message || "Failed to list measurement docs.");
      setFiles([]);
      setFileCursor(null);
      setHasMoreFiles(false);
    } finally {
      setFilesLoading(false);
    }
  }

  // when user changes, reset file list + selection
  useEffect(() => {
    setFiles([]);
    setNextToken(null);
    setSelectedFile(null);
    setActivityData(null);
    setDataErr("");

    if (selectedUser?.uid) loadFiles({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.uid]);

  async function openFile(file) {
    if (!file?.path) return;

    setSelectedFile(file);
    setDataLoading(true);
    setDataErr("");
    setActivityData(null);

    try {
      const snap = await getDoc(doc(db, file.path));
      if (!snap.exists()) throw new Error("Measurement doc not found.");

      const json = snap.data() || {};

      // prevent exertion modal popping in admin
      const safe = { ...json, activity_details_logged: true };
      setActivityData(safe);
    } catch (e) {
      setDataErr(e?.message || "Failed to load measurement doc.");
    } finally {
      setDataLoading(false);
    }
  }

  const selectedBaseName = selectedFile ? selectedFile.id : "";

  const selectedDate = activityData
    ? parseDateFromData(activityData)
    : new Date();
  const selectedKind =
    selectedFile && activityData
      ? detectKind({ filename: selectedFile.name, data: activityData })
      : "unknown";

  return (
    <div className="w-full max-w-6xl p-4 space-y-4">
      <div>
        <h1 className="title-text">Admin · Inspect Measurements</h1>
        <p className="muted-text">
          Storage path:{" "}
          <code>measurements/&lt;uid&gt;/breatheultra/&lt;file&gt;</code>
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Left panel: user + file list */}
        <div className="md:col-span-5 space-y-3">
          <div className="rounded-2xl border-2 border-greyscale-1 p-3 space-y-2">
            <div className="text-sm font-semibold">Select user</div>

            <input
              className="custom-input-on-dark w-full"
              placeholder="Search user by email prefix…"
              value={emailPrefix}
              onChange={(e) => setEmailPrefix(e.target.value)}
            />

            {usersLoading ? (
              <LoadingBouncyDots text="Searching users…" />
            ) : (
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {users.map((u) => (
                  <button
                    key={u.uid}
                    className={[
                      "w-full text-left rounded-xl p-2 border-2",
                      selectedUser?.uid === u.uid
                        ? "border-primary"
                        : "border-greyscale-1 hover:border-greyscale-2",
                    ].join(" ")}
                    onClick={() =>
                      setSelectedUser({ uid: u.uid, email: u.email || "" })
                    }
                  >
                    <div className="text-sm truncate">{u.email || "—"}</div>
                    <div className="text-xs text-typography/60 font-mono truncate">
                      {u.uid}
                    </div>
                  </button>
                ))}
                {!users.length && (
                  <div className="text-xs text-typography/60">
                    No users found.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="rounded-2xl border-2 border-greyscale-1 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Measurements</div>
              <button
                className="small-outlined-button"
                onClick={() => selectedUser?.uid && loadFiles({ reset: true })}
                disabled={!selectedUser?.uid || filesLoading}
              >
                Refresh
              </button>
            </div>

            {!selectedUser?.uid ? (
              <div className="text-xs text-typography/60">
                Select a user to load files.
              </div>
            ) : filesLoading && !files.length ? (
              <LoadingBouncyDots text="Listing files…" />
            ) : filesErr ? (
              <div className="text-xs text-red-400">{filesErr}</div>
            ) : (
              <>
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {files.map((f) => (
                    <button
                      key={f.fullPath}
                      className={[
                        "w-full text-left rounded-xl p-2 border-2",
                        selectedFile?.fullPath === f.fullPath
                          ? "border-primary"
                          : "border-greyscale-1 hover:border-greyscale-2",
                      ].join(" ")}
                      onClick={() => openFile(f)}
                    >
                      <div className="text-xs font-mono truncate">{f.id}</div>
                    </button>
                  ))}
                  {!files.length && (
                    <div className="text-xs text-typography/60">
                      No JSON files found in this folder.
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    className="outlined-button"
                    onClick={() => loadFiles({ reset: false })}
                    disabled={!hasMoreFiles || filesLoading}
                  >
                    {filesLoading
                      ? "Loading…"
                      : nextToken
                      ? "Load more"
                      : "No more"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right panel: renderer */}
        <div className="md:col-span-7 space-y-3">
          <div className="rounded-2xl border-2 border-greyscale-1 p-3">
            <div className="text-sm font-semibold">Inspection</div>
            <div className="text-xs text-typography/60">
              User:{" "}
              <span className="font-mono">{selectedUser?.uid || "—"}</span>
              {" · "}
              File:{" "}
              <span className="font-mono">{selectedFile?.name || "—"}</span>
              {" · "}
              Kind: <span className="font-mono">{selectedKind}</span>
            </div>
          </div>

          {dataLoading ? (
            <LoadingBouncyDots text="Loading measurement JSON…" />
          ) : dataErr ? (
            <div className="rounded-2xl border-2 border-red-400 p-3 text-xs text-red-400">
              {dataErr}
            </div>
          ) : !selectedUser?.uid || !selectedFile || !activityData ? (
            <div className="rounded-2xl border-2 border-greyscale-1 p-3 text-sm text-typography/70">
              Select a user, then select a measurement file to render results.
            </div>
          ) : selectedKind === "exertion" ? (
            <ExertionRecoveryActivityDetails
              uid={selectedUser.uid}
              baseName={selectedBaseName}
              date={selectedDate}
              data={activityData}
              locks={{}} // admin: no locks overlay
              redemptionCTA={null}
              downloadURL={null}
            />
          ) : selectedKind === "spirometry" ? (
            <SpirometryActivityDetails
              uid={selectedUser.uid}
              baseName={selectedBaseName}
              date={selectedDate}
              data={activityData}
              locks={{}} // admin: no locks overlay
              redemptionCTA={null}
              downloadURL={null}
            />
          ) : (
            <div className="rounded-2xl border-2 border-greyscale-1 p-3 space-y-2">
              <div className="text-sm">
                Could not determine measurement kind for this file.
              </div>
              <div className="text-xs text-typography/60">
                Tip: include “exertion” or “spirometry” in filenames, or add{" "}
                <code>{"kind"}</code> to the JSON.
              </div>
            </div>
          )}

          <Spacer />

          {/* Optional: raw JSON viewer */}
          {activityData && (
            <details className="rounded-2xl border-2 border-greyscale-1 p-3">
              <summary className="text-sm font-semibold cursor-pointer">
                Raw JSON
              </summary>
              <pre className="text-xs overflow-x-auto mt-2 whitespace-pre-wrap font-mono">
                {JSON.stringify(activityData, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
